import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { History, Link2, Loader2, MessageSquare, UserRound, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  commentApi, itsmApi,
  type ItsmCollection, type ItsmItem, type ItsmRelation, type ItsmTimelineEntry,
} from '../../services/api';
import {
  compactField, field, ghostButton, heading, help, label, muted, primaryButton, ring,
  secondaryButton, useAssigneeOptions, warnText,
} from './itsmUi';

/**
 * The working half of the ticket panel (PO46-1276): assign, relations, the
 * timeline. Each section owns its own loading and saving state and reports
 * through the one toast system; none of them blocks another.
 */

const errMsg = (err: unknown, fallback: string) =>
  (err instanceof Error && err.message) || fallback;

// ---- assign ----------------------------------------------------------------

export function AssignSection({ collection, item, knownAssignees, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  knownAssignees?: string[];
  onChanged: (updated?: ItsmItem) => void;
}) {
  const [value, setValue] = useState(item.assignee || '');
  const [saving, setSaving] = useState(false);
  const people = useAssigneeOptions(knownAssignees);
  const id = `assign-${item._id}`;
  const dirty = value.trim() !== (item.assignee || '');

  const save = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!dirty) return;
    setSaving(true);
    try {
      const updated = await itsmApi.update(collection, item._id, { assignee: value.trim() });
      toast.success(value.trim() ? `Assigned to ${value.trim()}. They have been told.` : 'Unassigned');
      onChanged(updated);
    } catch (err) {
      toast.error(errMsg(err, 'Could not assign it. Try again.'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save}>
      <label htmlFor={id} className={`${label} flex items-center gap-1.5`}>
        <UserRound size={12} aria-hidden="true" /> Assignee
      </label>
      <div className="flex gap-2">
        <input id={id} type="email" className={field} value={value} list={`${id}-options`}
          autoComplete="off" placeholder="Unassigned" aria-describedby={`${id}-help`}
          onChange={(e) => setValue(e.target.value)} />
        <datalist id={`${id}-options`}>
          {people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
        </datalist>
        <button type="submit" disabled={!dirty || saving} className={`${secondaryButton} shrink-0`}>
          {saving ? 'Saving…' : 'Assign'}
        </button>
      </div>
      <p id={`${id}-help`} className={help}>
        {item.ownerGroup ? `Queue: ${item.ownerGroupName || item.ownerGroup}. ` : 'No queue owns this ticket. '}
        The new assignee is told by email.
      </p>
    </form>
  );
}

// ---- relations ---------------------------------------------------------------

export function RelationsSection({ collection, item, relationTypes, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  relationTypes: string[];
  onChanged: (updated?: ItsmItem) => void;
}) {
  const [rel, setRel] = useState<{ outbound: ItsmRelation[]; inbound: ItsmRelation[] } | null>(null);
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState('');
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const base = `rel-${item._id}`;

  const load = useCallback(async () => {
    try {
      setRel(await itsmApi.relations(collection, item._id));
    } catch {
      setRel({ outbound: [], inbound: [] });
    }
  }, [collection, item._id]);

  useEffect(() => { void load(); }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!type || !target.trim()) return;
    setSaving(true);
    try {
      const updated = await itsmApi.link(collection, item._id, type, target.trim());
      toast.success('Linked. It shows on both tickets.');
      setTarget('');
      setAdding(false);
      await load();
      onChanged(updated);
    } catch (err) {
      toast.error(errMsg(err, 'Could not link it.'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: ItsmRelation) => {
    if (!r.targetId) return;
    try {
      const updated = await itsmApi.unlink(collection, item._id, r.type, r.targetId);
      toast.success('Unlinked');
      await load();
      onChanged(updated);
    } catch (err) {
      toast.error(errMsg(err, 'Could not unlink it.'));
    }
  };

  const empty = rel && rel.outbound.length === 0 && rel.inbound.length === 0;

  return (
    <section aria-labelledby={`${base}-h`}>
      <h3 id={`${base}-h`} className={`${heading} mb-2 flex items-center gap-1.5`}>
        <Link2 size={12} aria-hidden="true" /> Related
      </h3>
      {rel === null ? (
        <p className={`text-xs ${muted}`} role="status">Loading…</p>
      ) : empty ? (
        <p className={`text-xs ${muted}`}>
          No links yet. Link a problem this rolled up to, a change that fixes it, or a duplicate.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {rel.outbound.map((r) => (
            <li key={`o-${r.type}-${r.targetId}`} className="flex items-start gap-2 text-xs text-slate-800 dark:text-slate-200">
              <span className="min-w-0 flex-1">
                <span className={muted}>{r.type} →</span>{' '}
                {r.restricted ? (
                  <span className={muted}>an item you cannot see</span>
                ) : (
                  <><span className={`font-mono text-[11px] ${muted}`}>{r.targetKey}</span> {r.targetTitle}</>
                )}
              </span>
              <button type="button" onClick={() => remove(r)}
                aria-label={`Unlink ${r.type} ${r.targetKey || r.targetId}`}
                className={`shrink-0 text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 rounded ${ring}`}>
                <X size={12} aria-hidden="true" />
              </button>
            </li>
          ))}
          {/* Both directions: on the Problem, the Incidents that rolled up INTO it. */}
          {rel.inbound.map((r) => (
            <li key={`i-${r.type}-${r.sourceId}`} className="text-xs text-slate-800 dark:text-slate-200">
              <span className={`font-mono text-[11px] ${muted}`}>{r.sourceKey}</span>{' '}
              {r.sourceTitle} <span className={muted}>← {r.type}</span>
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <form onSubmit={add} className="mt-2 space-y-2 rounded-lg border border-slate-200 dark:border-slate-800 p-2.5">
          <div>
            <label htmlFor={`${base}-type`} className={label}>Relation</label>
            <select id={`${base}-type`} className={`${compactField} w-full`} value={type} required
              onChange={(e) => setType(e.target.value)}>
              <option value="">Choose a relation</option>
              {relationTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={`${base}-target`} className={label}>Other ticket's id</label>
            <input id={`${base}-target`} className={`${compactField} w-full font-mono`} value={target}
              required placeholder="incident:…" aria-describedby={`${base}-target-help`}
              onChange={(e) => setTarget(e.target.value)} />
            <p id={`${base}-target-help`} className={help}>
              The link is stored on this ticket and shown on both.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setAdding(false)} className={secondaryButton}>Cancel</button>
            <button type="submit" disabled={saving || !type || !target.trim()} className={primaryButton}>
              {saving ? 'Linking…' : 'Link'}
            </button>
          </div>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className={`mt-2 ${ghostButton}`}>
          + Add a relation
        </button>
      )}
    </section>
  );
}

// ---- outcome -----------------------------------------------------------------

export function OutcomeField({ id, outcomes, current, value, onChange, needed }: {
  id: string;
  outcomes: string[] | null;
  current?: string;
  value: string;
  onChange: (v: string) => void;
  needed: boolean;
}) {
  if (outcomes && outcomes.length === 0) return null;     // derived (Events)
  return (
    <div>
      <label htmlFor={id} className={label}>Outcome</label>
      <select id={id} className={field} value={value} disabled={outcomes === null}
        aria-describedby={`${id}-help`} onChange={(e) => onChange(e.target.value)}>
        <option value="">
          {outcomes === null ? 'Loading…' : current ? `${current} (recorded)` : 'Not set'}
        </option>
        {(outcomes || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <p id={`${id}-help`} className={needed && !value && !current ? `mt-1 ${warnText}` : help}>
        Required to close. The options come from this ticket's workflow.
      </p>
    </div>
  );
}

// ---- timeline ----------------------------------------------------------------

function describe(e: ItsmTimelineEntry): string {
  const d = (e.details || {}) as Record<string, unknown>;
  switch (e.action) {
    case 'created': return 'raised the ticket';
    case 'assigned': return d.to ? `assigned it to ${String(d.to)}` : 'unassigned it';
    case 'status_changed': return `moved it ${d.from ? `from ${String(d.from)} ` : ''}to ${String(d.to || '')}`;
    case 'updated': return 'edited it';
    case 'sla_warning': return `SLA warning: ${String(d.clock || '')} due ${String(d.dueAt || '').slice(0, 16)}`;
    case 'sla_breach': return `SLA breached: ${String(d.clock || '')}`;
    default: return (e.action || 'changed it').replace(/_/g, ' ');
  }
}

export function TimelineSection({ collection, item, refreshKey }: {
  collection: ItsmCollection;
  item: ItsmItem;
  /** Changes when the panel changed the ticket, so the timeline reloads. */
  refreshKey?: string;
}) {
  const [entries, setEntries] = useState<ItsmTimelineEntry[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);
  const base = `tl-${item._id}`;

  const load = useCallback(async () => {
    try {
      const r = await itsmApi.timeline(collection, item._id);
      setEntries(r.entries || []);
      setFailed(false);
    } catch {
      setFailed(true);
      setEntries([]);
    }
  }, [collection, item._id]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const post = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    try {
      await commentApi.create(item._id, { body: body.trim() });
      setBody('');
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not add the comment.'), { duration: 8000 });
    } finally {
      setPosting(false);
    }
  };

  return (
    <section aria-labelledby={`${base}-h`}>
      <h3 id={`${base}-h`} className={`${heading} mb-2 flex items-center gap-1.5`}>
        <History size={12} aria-hidden="true" /> Timeline
      </h3>
      {entries === null ? (
        <p className={`text-xs ${muted} flex items-center gap-1.5`} role="status">
          <Loader2 size={12} className="animate-spin motion-reduce:animate-none" aria-hidden="true" /> Loading…
        </p>
      ) : failed ? (
        <p className={warnText} role="status">The timeline could not be loaded. Close and reopen the ticket to try again.</p>
      ) : entries.length === 0 ? (
        <p className={`text-xs ${muted}`}>Nothing has happened yet. Comments and moves appear here.</p>
      ) : (
        <ol className="space-y-2 border-l border-slate-200 dark:border-slate-800 pl-3">
          {entries.map((e) => (
            <li key={e.id} className="text-xs">
              <p className="text-slate-800 dark:text-slate-200">
                {e.kind === 'comment'
                  ? <MessageSquare size={11} className="inline mr-1 -mt-0.5" aria-label="Comment" />
                  : null}
                <span className="font-medium">{e.actor || 'Someone'}</span>{' '}
                {e.kind === 'comment' ? 'commented' : describe(e)}
              </p>
              {e.kind === 'comment' && (
                <p className="mt-0.5 whitespace-pre-wrap text-slate-700 dark:text-slate-300">{e.body}</p>
              )}
              <time dateTime={e.at} className={`text-[11px] ${muted}`}>
                {e.at ? new Date(e.at).toLocaleString() : ''}
              </time>
            </li>
          ))}
        </ol>
      )}
      <form onSubmit={post} className="mt-3">
        <label htmlFor={`${base}-comment`} className={label}>Add a comment</label>
        <textarea id={`${base}-comment`} className={field} rows={2} value={body}
          onChange={(e) => setBody(e.target.value)} />
        <div className="mt-2 flex justify-end">
          <button type="submit" disabled={posting || !body.trim()} className={primaryButton}>
            {posting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </form>
    </section>
  );
}
